import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { PageTitle } from "../../components/Layout";
import { useAuth } from "../../context/AuthContext";
import { richHtml } from "../../lib/sanitize";
import * as Icons from "lucide-react";

export default function ClientHelp() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("Toutes");
  const [open, setOpen] = useState(null);
  useEffect(() => {
    api.get("/faqs").then((r) => setItems(r.data));
    api.get("/faq-categories").then((r) => setCats(r.data)).catch(() => {});
  }, []);

  const catNames = useMemo(() => cats.map((c) => c.name), [cats]);
  const catMap = useMemo(() => Object.fromEntries(cats.map((c) => [c.name, c])), [cats]);
  const filtered = items.filter((f) =>
    (filter === "Toutes" || (f.category || "") === filter) &&
    (!q || f.question.toLowerCase().includes(q.toLowerCase()) || (f.answer || "").toLowerCase().includes(q.toLowerCase())));

  const groups = useMemo(() => {
    const order = [...catNames, "Sans catégorie"];
    const g = {};
    filtered.forEach((f) => { const c = f.category || "Sans catégorie"; (g[c] = g[c] || []).push(f); });
    return Object.entries(g).sort((a, b) => (order.indexOf(a[0]) === -1 ? 99 : order.indexOf(a[0])) - (order.indexOf(b[0]) === -1 ? 99 : order.indexOf(b[0])));
  }, [filtered, catNames]);

  return (
    <div className="max-w-3xl">
      <PageTitle overline="Centre d'aide" title="Aide & FAQ" />
      <p className="text-sm text-marine/80 mb-6">Retrouvez les réponses aux questions les plus fréquentes. Une question sans réponse ? Contactez votre équipe Metara depuis « Demandes & Messages ».</p>
      {user?.role === "client" && (
        <div className="bg-sable rounded-sm border border-marine/10 shadow-sm p-5 mb-8 flex items-center justify-between gap-4" data-testid="relaunch-tour-card">
          <div>
            <div className="text-sm font-semibold text-marine">Visite guidée du portail</div>
            <p className="text-xs text-marine/80 mt-0.5">Retrouvez une présentation des principales fonctionnalités de votre espace.</p>
          </div>
          <button type="button" onClick={() => window.dispatchEvent(new Event("launch-guided-tour"))} data-testid="relaunch-tour-btn"
            className="text-sm bg-marine text-white rounded-sm px-4 py-2 hover:bg-marine-hover transition-colors shrink-0">
            Relancer
          </button>
        </div>
      )}
      <div className="relative mb-4">
        <Icons.Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-marine" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="faq-search" placeholder="Rechercher une question..." className="w-full pl-9 pr-3 py-2.5 rounded-sm border border-marine/20 text-sm outline-none focus:ring-1 focus:ring-marine" />
      </div>
      {catNames.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-8" data-testid="faq-cat-tabs">
          {["Toutes", ...catNames].map((c) => (
            <button key={c} onClick={() => setFilter(c)} data-testid={`faq-tab-${c}`}
              className={`px-3 py-1.5 rounded-sm text-xs border transition-colors ${filter === c ? "bg-marine text-white border-marine" : "border-marine/20 text-marine hover:bg-white"}`}>{c}</button>
          ))}
        </div>
      )}
      {groups.length === 0 && <p className="text-sm text-marine/80" data-testid="faq-empty">Aucune réponse disponible pour le moment.</p>}
      <div className="space-y-8">
        {groups.map(([cat, list]) => (
          <section key={cat} data-testid={`faq-group-${cat}`}>
            <h2 className="font-display text-lg text-marine mb-1">{cat}</h2>
            {catMap[cat]?.description && <p className="text-sm text-marine/80 mb-3" data-testid={`faq-cat-desc-${cat}`}>{catMap[cat].description}</p>}
            <div className="space-y-3 mt-3">
              {list.map((f) => {
                const isOpen = open === f.id;
                return (
                  <div key={f.id} data-testid={`faq-card-${f.id}`} className={`bg-sable rounded-sm border transition-colors ${isOpen ? "border-marine/40 shadow-sm" : "border-marine/10"}`}>
                    <button onClick={() => setOpen(isOpen ? null : f.id)} data-testid={`faq-toggle-${f.id}`} className="w-full text-left flex items-center justify-between gap-3 p-5">
                      <span className="font-medium text-marine">{f.question}</span>
                      <Icons.ChevronDown size={16} className={`text-bordeaux shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                    </button>
                    {isOpen && <div className="px-5 pb-5 rich-content border-t border-marine/10 pt-3" data-testid={`faq-answer-${f.id}`} dangerouslySetInnerHTML={richHtml(f.answer)} />}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
